import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@/shared/components/Icon/Icon'
import {
  ShieldCheckIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  GlobeAltIcon,
  WalletIcon,
  FireIcon,
  CheckBadgeIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
} from 'framer-motion'
import { useSEO, useOrganizationSchema, useWebsiteSchema, useFAQSchema, useSoftwareApplicationSchema } from '@/shared/hooks/useSEO'
/* ═══════════ ANIMATION VARIANTS ═══════════ */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const } },
}

/* ═══════════ MAGNETIC BUTTON ═══════════ */
function MagneticButton({ children, className = '', href, download }: { children: React.ReactNode; className?: string; href?: string; download?: string }) {
  const ref = useRef<HTMLAnchorElement>(null)
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { stiffness: 150, damping: 15 })
  const springY = useSpring(y, { stiffness: 150, damping: 15 })

  return (
    <motion.a
      ref={ref}
      href={href}
      download={download}
      style={{ x: springX, y: springY }}
      onMouseMove={(e) => {
        if (!ref.current) return
        const rect = ref.current.getBoundingClientRect()
        x.set((e.clientX - (rect.left + rect.width / 2)) * 0.15)
        y.set((e.clientY - (rect.top + rect.height / 2)) * 0.15)
      }}
      onMouseLeave={() => { x.set(0); y.set(0) }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      className={className}
    >
      {children}
    </motion.a>
  )
}

/* ═══════════ STAR RATING ═══════════ */
/* ═══════════ FAQ ═══════════ */
/* ═══════════ MAIN PAGE ═══════════ */
export function LandingPage() {
  useSEO()
  useOrganizationSchema()
  useWebsiteSchema()
  useSoftwareApplicationSchema()
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'

  const heroRef = useRef(null)

  const features = [
    { icon: CreditCardIcon, title: t('features.items.0.title'), desc: t('features.items.0.desc') },
    { icon: WalletIcon, title: t('features.items.1.title'), desc: t('features.items.1.desc') },
    { icon: GlobeAltIcon, title: t('features.items.2.title'), desc: t('features.items.2.desc') },
    { icon: FireIcon, title: t('features.items.3.title'), desc: t('features.items.3.desc') },
  ]

  const testimonials = [
    { name: t('testimonials.items.0.name'), role: t('testimonials.items.0.role'), text: t('testimonials.items.0.text'), rating: 5 },
    { name: t('testimonials.items.1.name'), role: t('testimonials.items.1.role'), text: t('testimonials.items.1.text'), rating: 5 },
    { name: t('testimonials.items.2.name'), role: t('testimonials.items.2.role'), text: t('testimonials.items.2.text'), rating: 5 },
  ]

  const faqItems = [
    { q: t('faq.items.0.q'), a: t('faq.items.0.a') },
    { q: t('faq.items.1.q'), a: t('faq.items.1.a') },
    { q: t('faq.items.2.q'), a: t('faq.items.2.a') },
    { q: t('faq.items.3.q'), a: t('faq.items.3.a') },
    { q: t('faq.items.4.q'), a: t('faq.items.4.a') },
    { q: t('faq.items.5.q'), a: t('faq.items.5.a') },
  ]
  useFAQSchema(faqItems.map(item => ({ question: item.q, answer: item.a })))

  return (
    <>
      {/* ═══════════════════════════════════════════════════════
          SECTION 1 — HERO (Light, centered, phone below)
          ═══════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative py-24 md:py-32 overflow-hidden">
        {/* Grid pattern */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.3]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(229,217,200,0.7) 40px), repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(229,217,200,0.7) 40px)', backgroundSize: '40px 40px' }} />
        {/* Ambient glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-wine-300/8 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-marble-100 border border-marble-200 shadow-sm mb-8"
          >
            <span className="h-2 w-2 rounded-full bg-wine-800 animate-pulse" />
            <span className="text-xs font-semibold text-ink-2 tracking-wide">{t('hero.badge')}</span>
          </motion.div>

          {/* Headline */}
          <h1 className="text-[clamp(2.2rem,5vw,4rem)] font-extrabold leading-[1.1] tracking-tight text-ink mb-5">
            <span className="block overflow-hidden">
              {t('hero.title1').split(' ').map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ y: '100%', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mx-[0.12em]"
                >
                  {word}
                </motion.span>
              ))}
            </span>
            <span className="block overflow-hidden">
              {t('hero.title2').split(' ').map((word, i) => (
                <motion.span
                  key={i}
                  initial={{ y: '100%', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className="inline-block mx-[0.12em] text-wine-800"
                >
                  {word}
                </motion.span>
              ))}
            </span>
          </h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="text-ink-2 text-base sm:text-lg max-w-xl mx-auto font-light leading-relaxed mb-10"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.65 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
          >
            <MagneticButton
              href="/download"
              download="sakk-wallet.apk"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-wine-800 text-white font-bold text-sm rounded-2xl hover:bg-wine-700 shadow-xl shadow-wine-900/10 hover:shadow-wine-900/20 transition-all"
            >
              <Icon icon={DevicePhoneMobileIcon} size="sm" />
              {t('hero.downloadCta')}
              {isAr
                ? <Icon icon={ArrowRightIcon} size="sm" className="transition-transform group-hover:translate-x-1" />
                : <Icon icon={ArrowLeftIcon} size="sm" className="transition-transform group-hover:-translate-x-1" />
              }
            </MagneticButton>
            <motion.a
              href="#features"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-6 py-4 text-sm font-medium text-wine-800 hover:text-wine-900 rounded-2xl border border-wine-200 hover:border-wine-300 hover:bg-marble-100 transition-all"
            >
              {t('hero.exploreFeatures')}
            </motion.a>
          </motion.div>

          {/* Trust row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.8 }}
            className="flex items-center justify-center gap-6 text-xs text-ink-hint"
          >
            {[
              { icon: ShieldCheckIcon, text: 'PCI DSS' },
              { icon: LockClosedIcon, text: '256-bit' },
              { icon: CheckBadgeIcon, text: 'Stripe' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                {i > 0 && <div className="w-px h-3 bg-wine-100/30 me-2" />}
                <Icon icon={item.icon} size="xs" className="text-ink-2" />
                <span>{item.text}</span>
              </div>
            ))}
          </motion.div>
        </div>

      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 2 — CAPABILITIES SHOWCASE
          ═══════════════════════════════════════════════════════ */}
              <section className="py-24 md:py-32 bg-marble-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {[
              {
                icon: CreditCardIcon,
                title: t('features.items.0.title'),
                desc: t('features.items.0.desc'),
              },
              {
                icon: WalletIcon,
                title: t('features.items.1.title'),
                desc: t('features.items.1.desc'),
              },
              {
                icon: GlobeAltIcon,
                title: t('features.items.2.title'),
                desc: t('features.items.2.desc'),
              },
              {
                icon: FireIcon,
                title: t('features.items.3.title'),
                desc: t('features.items.3.desc'),
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                whileHover={{ y: -6, transition: { duration: 0.25 } }}
                className="relative p-7 rounded-3xl bg-marble-100 border border-wine-100/40 hover:border-wine-200 hover:shadow-lg hover:shadow-wine-900/5 transition-all overflow-hidden group"
              >
                <div className="h-14 w-14 rounded-2xl bg-wine-50 flex items-center justify-center mb-5 relative z-10">
                  <Icon icon={item.icon} size="md" className="text-wine-700" />
                </div>
                <h2 className="text-base font-bold text-ink mb-2 relative z-10">{item.title}</h2>
                <p className="text-sm text-ink-2 leading-relaxed relative z-10">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 3 — VIRTUAL CARD SHOWCASE
          ═══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            {/* Left — Text + features */}
            <motion.div
              initial={{ opacity: 0, x: isAr ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-wine-800 bg-wine-50 rounded-full border border-wine-200/40 mb-5">
                {t('virtualCard.label')}
              </span>
              <h2 className="text-3xl md:text-[2.8rem] font-extrabold text-ink leading-tight tracking-tight mb-4">
                {t('virtualCard.title')}
                <br />
                <span className="text-wine-800">{t('virtualCard.titleHighlight')}</span>
              </h2>
              <p className="text-ink-2 leading-relaxed mb-8 max-w-md">
                {t('virtualCard.subtitle')}
              </p>

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8"
              >
                {[
                  { icon: BoltIcon, title: t('virtualCard.features.0.title'), desc: t('virtualCard.features.0.desc') },
                  { icon: ArrowTrendingUpIcon, title: t('virtualCard.features.1.title'), desc: t('virtualCard.features.1.desc') },
                  { icon: ShieldCheckIcon, title: t('virtualCard.features.2.title'), desc: t('virtualCard.features.2.desc') },
                  { icon: GlobeAltIcon, title: t('virtualCard.features.3.title'), desc: t('virtualCard.features.3.desc') },
                ].map((f, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="flex items-start gap-3 p-4 rounded-xl bg-marble-100/60 border border-wine-100/40 hover:border-wine-200 hover:bg-marble-100 transition-all"
                  >
                    <div className="shrink-0 h-9 w-9 rounded-lg bg-wine-50 flex items-center justify-center">
                      <Icon icon={f.icon} size="xs" className="text-wine-700" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-ink mb-0.5">{f.title}</h3>
                      <p className="text-xs text-ink-2 leading-relaxed">{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <a
                href="#download"
                className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-wine-800 text-white text-sm font-semibold rounded-xl hover:bg-wine-700 transition-all"
              >
                <Icon icon={CreditCardIcon} size="sm" className="text-white" />
                {t('virtualCard.cta')}
              </a>
            </motion.div>

            {/* Right — Card visual */}
            <motion.div
              initial={{ opacity: 0, x: isAr ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="relative flex items-center justify-center"
            >
              {/* CSS Card */}
              <div
                className="relative w-full max-w-[400px] aspect-[1.586/1] rounded-3xl shadow-2xl shadow-wine-950/30 p-7 sm:p-8 flex flex-col justify-between overflow-hidden group hover:scale-[1.03] transition-transform duration-500 bg-gradient-to-br from-wine-800 to-wine-950"
              >
                {/* Damascene medallion */}
                <svg className="absolute -end-12 -top-12 w-48 h-48 opacity-[0.06] pointer-events-none" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="100" cy="100" r="90" stroke="currentColor" strokeWidth="0.5" className="text-white" />
                  <circle cx="100" cy="100" r="70" stroke="currentColor" strokeWidth="0.5" className="text-white" />
                  <circle cx="100" cy="100" r="50" stroke="currentColor" strokeWidth="0.5" className="text-white" />
                  <path d="M100 10 L100 190 M10 100 L190 100 M29 29 L171 171 M171 29 L29 171" stroke="currentColor" strokeWidth="0.3" className="text-white" />
                  {[0,45,90,135,180,225,270,315].map((deg) => (
                    <circle key={deg} cx={100 + 80 * Math.cos(deg * Math.PI / 180)} cy={100 + 80 * Math.sin(deg * Math.PI / 180)} r="8" stroke="currentColor" strokeWidth="0.4" fill="none" className="text-white" />
                  ))}
                  {[0,60,120,180,240,300].map((deg) => (
                    <path key={deg} d={`M100 100 L${100 + 60 * Math.cos(deg * Math.PI / 180)} ${100 + 60 * Math.sin(deg * Math.PI / 180)}`} stroke="currentColor" strokeWidth="0.3" className="text-white" />
                  ))}
                </svg>
                {/* Subtle light reflection */}
                <div className="absolute top-0 left-0 w-full h-[45%] bg-gradient-to-b from-white/[0.07] via-white/[0.02] to-transparent pointer-events-none rounded-t-3xl" />

                {/* Card top — brand + chip */}
                <div className="flex items-start justify-between relative z-10" dir="ltr">
                  <div>
                    <p className="text-2xl font-bold text-white tracking-wide" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>صك</p>
                    <p className="text-[9px] text-white/35 tracking-[0.2em] mt-1 uppercase">SAKK WALLET</p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="w-11 h-8 rounded-md bg-gold-400 shadow-inner relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent" />
                    </div>
                  </div>
                </div>

                {/* Card number */}
                <div className="relative z-10 mt-2" dir="ltr">
                  <p className="text-[1.15rem] sm:text-[1.3rem] tracking-[0.22em] text-white/90 font-medium font-en text-left" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                    4532 7821 3356 8901
                  </p>
                </div>

                {/* Card bottom — holder + expiry + brand */}
                <div className="flex items-end justify-between relative z-10 mt-1" dir="ltr">
                  <div className="flex items-end gap-7">
                    <div>
                      <p className="text-[8px] uppercase tracking-[0.18em] text-white/30 mb-1">VALID THRU</p>
                      <p className="text-[13px] tracking-[0.12em] text-white/80 font-semibold font-en">12/28</p>
                    </div>
                    <div>
                      <p className="text-[8px] uppercase tracking-[0.18em] text-white/30 mb-1">CARDHOLDER</p>
                      <p className="text-[13px] tracking-[0.1em] text-white/80 font-semibold font-en uppercase">AHMAD KHOURY</p>
                    </div>
                  </div>
                  {/* Visa brand mark */}
                  <div className="text-white/50 font-bold text-lg font-en italic tracking-wider">VISA</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 4 — FEATURES (Premium alternating showcase)
          ═══════════════════════════════════════════════════════ */}
      <section id="features" className="py-24 md:py-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-20"
          >
            <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-wine-800 bg-wine-50 rounded-full border border-wine-200/40 mb-5">
              {t('features.label')}
            </span>
            <h2 className="text-3xl md:text-[2.8rem] font-extrabold text-ink leading-tight tracking-tight">
              {t('features.title')}
              <br />
              <span className="text-wine-800">{t('features.titleHighlight')}</span>
            </h2>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                ...features[0],
                num: '01',
              },
              {
                ...features[1],
                num: '02',
              },
              {
                ...features[2],
                num: '03',
              },
              {
                ...features[3],
                num: '04',
              },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[2rem] overflow-hidden border border-wine-100/40 hover:border-wine-200 bg-marble-100 transition-colors"
              >
                <div className={`relative flex flex-col ${i % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'} items-center gap-8 sm:gap-12 p-8 sm:p-10 md:p-12`}>
                  {/* Icon side */}
                  <div className="shrink-0 flex items-center justify-center">
                    <div className="relative">
                      <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-3xl bg-wine-50 flex items-center justify-center">
                        <Icon icon={f.icon} size="xl" className="text-wine-700" />
                      </div>
                      {/* Number badge */}
                      <div className="absolute -top-2 -end-2 h-7 w-7 rounded-full bg-marble-100 shadow-md flex items-center justify-center text-[10px] font-bold text-wine-800 font-en">
                        {f.num}
                      </div>
                    </div>
                  </div>

                  {/* Text side */}
                  <div className="flex-1 text-center sm:text-start">
                    <h3 className="text-xl sm:text-2xl font-extrabold text-ink mb-3 leading-snug">{f.title}</h3>
                    <p className="text-sm sm:text-[0.95rem] text-ink-2 leading-relaxed max-w-lg">{f.desc}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-wine-800">
                      <span>{isAr ? 'المزيد' : 'Learn more'}</span>
                      <Icon icon={isAr ? ArrowLeftIcon : ArrowRightIcon} size="xs" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 5 — BUILT FOR YOUR LIFESTYLE (User personas)
          ═══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-wine-800 bg-wine-50 rounded-full border border-wine-200/40 mb-5">
              {t('lifestyle.label')}
            </span>
            <h2 className="text-3xl md:text-[2.8rem] font-extrabold text-ink leading-tight tracking-tight">
              {t('lifestyle.title')}
              <br />
              <span className="text-wine-800">{t('lifestyle.titleHighlight')}</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: CurrencyDollarIcon,
                title: t('lifestyle.personas.0.title'),
                desc: t('lifestyle.personas.0.desc'),
                quote: t('lifestyle.personas.0.quote'),
                name: t('lifestyle.personas.0.name'),
                tagColor: 'bg-wine-800 text-wine-100',
              },
              {
                icon: GlobeAltIcon,
                title: t('lifestyle.personas.1.title'),
                desc: t('lifestyle.personas.1.desc'),
                quote: t('lifestyle.personas.1.quote'),
                name: t('lifestyle.personas.1.name'),
                tagColor: 'bg-wine-800 text-wine-100',
              },
              {
                icon: ShoppingCartIcon,
                title: t('lifestyle.personas.2.title'),
                desc: t('lifestyle.personas.2.desc'),
                quote: t('lifestyle.personas.2.quote'),
                name: t('lifestyle.personas.2.name'),
                tagColor: 'bg-wine-800 text-wine-100',
              },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -8, transition: { duration: 0.25 } }}
                className="relative rounded-3xl bg-wine-900 p-8 flex flex-col min-h-[420px] overflow-hidden group"
              >
                {/* Decorative circles */}
                <div className="absolute -top-10 -end-10 w-40 h-40 rounded-full border border-white/[0.05] pointer-events-none" />
                <div className="absolute -bottom-8 -start-8 w-32 h-32 rounded-full border border-white/[0.03] pointer-events-none" />
                {/* Tag */}
                <div className={`inline-flex self-start px-3 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase ${p.tagColor} mb-6`}>
                  {t(`lifestyle.personas.${i}.tag`)}
                </div>

                {/* Icon */}
                <div className="h-16 w-16 rounded-2xl bg-wine-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon icon={p.icon} size="lg" className="text-white" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-3">{p.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-6 flex-1">{p.desc}</p>

                {/* Quote */}
                <div className="pt-5 border-t border-white/10">
                  <p className="text-sm text-white/50 italic mb-2 leading-relaxed">"{p.quote}"</p>
                  <p className="text-xs font-semibold text-white/70">{p.name}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 6 — TRUSTED & SECURE (Badges + guarantees)
          ═══════════════════════════════════════════════════════ */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-14"
          >
            <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-wine-800 bg-wine-50 rounded-full border border-wine-200/40 mb-5">
              {t('trust.label')}
            </span>
            <h2 className="text-3xl md:text-[2.8rem] font-extrabold text-ink leading-tight tracking-tight">
              {t('trust.title')}
              <br />
              <span className="text-wine-800">{t('trust.titleHighlight')}</span>
            </h2>
          </motion.div>

          {/* Main trust grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {[
              {
                icon: ShieldCheckIcon,
                title: t('trust.items.0.title'),
                desc: t('trust.items.0.desc'),
              },
              {
                icon: LockClosedIcon,
                title: t('trust.items.1.title'),
                desc: t('trust.items.1.desc'),
              },
              {
                icon: CheckBadgeIcon,
                title: t('trust.items.2.title'),
                desc: t('trust.items.2.desc'),
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="relative p-7 rounded-3xl bg-marble-100 border border-wine-100/40 hover:shadow-lg hover:shadow-wine-900/5 transition-all overflow-hidden group"
              >
                <div className="h-12 w-12 rounded-2xl bg-wine-800 flex items-center justify-center shadow-md mb-5">
                  <Icon icon={item.icon} size="md" className="text-white" />
                </div>
                <h3 className="text-base font-bold text-ink mb-2">{item.title}</h3>
                <p className="text-sm text-ink-2 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Compliance strip */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center gap-4 flex-wrap"
          >
            {[
              { text: 'PCI DSS Level 1', icon: ShieldCheckIcon },
              { text: '256-bit SSL', icon: LockClosedIcon },
              { text: 'PCI DSS', icon: CheckBadgeIcon },
              { text: t('trustBar.stripeSecured'), icon: ShieldCheckIcon },
            ].map((b, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-wine-50/50 border border-wine-100/40 hover:border-wine-200 hover:shadow-sm transition-all"
              >
                <Icon icon={b.icon} size="xs" className="text-wine-600" />
                <span className="text-[11px] font-semibold text-wine-600">{b.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 7 — SPOTLIGHT TESTIMONIALS
          ═══════════════════════════════════════════════════════ */}
      {(() => {
        const [active, setActive] = useState(0)
        const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

        const resetTimer = useCallback(() => {
          if (intervalRef.current) clearInterval(intervalRef.current)
          intervalRef.current = setInterval(() => {
            setActive((p) => (p + 1) % testimonials.length)
          }, 6000)
        }, [testimonials.length])

        useEffect(() => { resetTimer(); return () => { if (intervalRef.current) clearInterval(intervalRef.current) } }, [resetTimer])

        const go = (i: number) => { setActive(i); resetTimer() }

        return (
          <section className="relative bg-wine-950 overflow-hidden">
            {/* Noise texture */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
              <filter id="testimonial-noise">
                <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
                <feColorMatrix type="saturate" values="0" />
              </filter>
              <rect width="100%" height="100%" filter="url(#testimonial-noise)" opacity="0.03" />
            </svg>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 md:py-28">
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-16"
              >
                <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-ink-hint bg-white/[0.06] rounded-full border border-white/[0.08] mb-5">
                  {t('testimonials.label')}
                </span>
                <h2 className="text-3xl md:text-[2.8rem] font-extrabold text-white leading-tight tracking-tight">
                  {t('testimonials.title')}
                  <br />
                  <span className="text-gold-400">{t('testimonials.titleHighlight')}</span>
                </h2>
              </motion.div>

              {/* ═══ Main spotlight area ═══ */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10 items-center">
                {/* Left — Big spotlight quote */}
                <div className="relative min-h-[320px] flex flex-col justify-center">
                  {/* Large decorative quote mark */}
                  <div className="absolute -top-4 start-0 text-[140px] md:text-[200px] font-serif text-white/[0.03] select-none leading-none pointer-events-none" aria-hidden="true">
                    {isAr ? '\u201D' : '\u201C'}
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={active}
                      initial={{ opacity: 0, x: isAr ? 30 : -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: isAr ? -30 : 30 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="relative z-10"
                    >
                      {/* Stars */}
                      <div className="flex gap-1 mb-6">
                        {[...Array(testimonials[active].rating)].map((_, j) => (
                          <svg key={j} className="w-5 h-5 text-gold-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>

                      {/* Quote text */}
                      <p className="text-xl md:text-2xl lg:text-[1.7rem] font-medium text-white/70 leading-[1.7] mb-8 max-w-2xl">
                        {testimonials[active].text}
                      </p>

                      {/* Author row */}
                      <div className="flex items-center gap-4">
                        <div className="h-14 w-14 rounded-2xl bg-wine-800 flex items-center justify-center text-white text-lg font-bold">
                          {testimonials[active].name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-base font-bold text-white">{testimonials[active].name}</p>
                          <p className="text-sm text-white/30">{testimonials[active].role}</p>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Progress bar */}
                  <div className="mt-10 flex gap-1.5">
                    {testimonials.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => go(i)}
                        className="h-1 flex-1 max-w-[60px] rounded-full overflow-hidden bg-white/[0.06] cursor-pointer group"
                        aria-label={`Testimonial ${i + 1}`}
                      >
                        {i === active ? (
                          <motion.div
                            key={`bar-${active}`}
                            className="h-full bg-gold-400 rounded-full"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 6, ease: 'linear' }}
                            style={{ transformOrigin: isAr ? 'right' : 'left' }}
                          />
                        ) : (
                          <div className={`h-full rounded-full transition-colors ${i < active ? 'bg-gold-400/40' : 'bg-transparent group-hover:bg-white/10'}`} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Right — Vertical tab stack */}
                <div className="flex flex-col gap-3">
                  {testimonials.map((item, i) => (
                    <motion.button
                      key={i}
                      onClick={() => go(i)}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                      className={`relative text-start p-5 rounded-2xl border transition-all duration-300 cursor-pointer ${
                        i === active
                          ? 'bg-white/[0.07] border-gold-400/20 shadow-lg'
                          : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.04] hover:border-white/[0.08]'
                      }`}
                    >
                      {/* Active indicator line */}
                      {i === active && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute inset-y-4 start-0 w-[3px] rounded-full bg-gold-400"
                          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        />
                      )}

                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${
                          i === active
                            ? 'bg-wine-800 text-white'
                            : 'bg-white/[0.06] text-white/30'
                        }`}>
                          {item.name.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-semibold truncate transition-colors ${i === active ? 'text-white' : 'text-white/50'}`}>
                            {item.name}
                          </p>
                          <p className="text-[11px] text-white/25 truncate">{item.role}</p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* ═══ Bottom stats bar ═══ */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-16 pt-8 border-t border-white/[0.05] grid grid-cols-2 md:grid-cols-4 gap-6"
              >
                {[
                  { value: '10K+', label: isAr ? 'مستخدم نشط' : 'Active Users', icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-ink-hint mx-auto mb-2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" /></svg> },
                  { value: '4.8', label: isAr ? 'تقييم المتجر' : 'Store Rating', icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-ink-hint mx-auto mb-2"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" /></svg> },
                  { value: '50K+', label: isAr ? 'مراجعة موثقة' : 'Verified Reviews', icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-ink-hint mx-auto mb-2"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" /></svg> },
                  { value: '99.9%', label: isAr ? 'وقت التشغيل' : 'Uptime', icon: <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-ink-hint mx-auto mb-2"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" /></svg> },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    {s.icon}
                    <span className="text-2xl md:text-3xl font-extrabold text-white font-en">{s.value}</span>
                    <p className="text-[11px] text-white/30 mt-1">{s.label}</p>
                  </div>
                ))}
              </motion.div>

              {/* ═══ Scrolling micro-testimonials ticker ═══ */}
              <div className="mt-12 overflow-hidden mask-image-linear">
                <motion.div
                  animate={{ x: isAr ? ['0%', '50%'] : ['50%', '0%'] }}
                  transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                  className="flex gap-4 w-max"
                >
                  {[...testimonials, ...testimonials].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/[0.03] border border-white/[0.05] shrink-0">
                      <div className="h-7 w-7 rounded-full bg-white/[0.06] flex items-center justify-center text-[10px] font-bold text-white/40">
                        {item.name.charAt(0)}
                      </div>
                      <span className="text-xs text-white/30 whitespace-nowrap max-w-[200px] truncate">{item.text}</span>
                      <span className="text-[10px] text-white/15 shrink-0">— {item.name}</span>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </section>
        )
      })()}

      {/* ═══════════════════════════════════════════════════════
          SECTION 8 — FAQ (3D Flip Cards — bento grid)
          ═══════════════════════════════════════════════════════ */}
      <section id="faq" className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-wine-800 bg-wine-50 rounded-full border border-wine-200/40 mb-5">
              {t('faq.label')}
            </span>
            <h2 className="text-3xl md:text-[2.8rem] font-extrabold text-ink leading-tight tracking-tight">
              {t('faq.title')}
              <br />
              <span className="text-wine-800">{t('faq.titleHighlight')}{t('faq.titleSuffix')}</span>
            </h2>
          </motion.div>

          {/* ═══ 3D Flip Cards Grid ═══ */}
          {(() => {
            const [flipped, setFlipped] = useState<Set<number>>(new Set())
            const toggle = (i: number) => setFlipped((prev) => {
              const next = new Set(prev)
              next.has(i) ? next.delete(i) : next.add(i)
              return next
            })

            const icons = [
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" /></svg>,
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" /></svg>,
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>,
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" /></svg>,
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0Z" /></svg>,
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" /></svg>,
            ]

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" style={{ perspective: '1200px' }}>
                {faqItems.map((item, i) => {
                  const isFlipped = flipped.has(i)
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.15 }}
                      transition={{ duration: 0.5, delay: i * 0.07 }}
                      className="group"
                    >
                      <div
                        onClick={() => toggle(i)}
                        className="relative cursor-pointer w-full h-[280px]"
                        style={{ transformStyle: 'preserve-3d' }}
                      >
                        {/* ═══ Front ═══ */}
                        <div
                          className="absolute inset-0 rounded-3xl p-7 flex flex-col justify-between bg-wine-900 border border-wine-800/30 shadow-xl transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                          style={{
                            backfaceVisibility: 'hidden',
                            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                          }}
                        >
                          {/* Icon */}
                          <div className="h-14 w-14 rounded-2xl bg-wine-800/50 flex items-center justify-center text-wine-200 group-hover:text-wine-100 group-hover:bg-wine-800/70 transition-all">
                            {icons[i]}
                          </div>

                          {/* Number */}
                          <div className="absolute top-7 end-7 text-[64px] font-extrabold text-white/[0.04] font-en leading-none select-none">
                            {String(i + 1).padStart(2, '0')}
                          </div>

                          {/* Question */}
                          <div>
                            <p className="text-white/90 text-[15px] font-semibold leading-relaxed mb-4">
                              {item.q}
                            </p>
                            <div className="flex items-center gap-2 text-gold-400/60 text-xs font-medium">
                              <span>{isAr ? 'اضغط للإجابة' : 'Tap for answer'}</span>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15m0 0 6.75-6.75M3.75 12h15m0 0-6.75 6.75" />
                              </svg>
                            </div>
                          </div>
                        </div>

                        {/* ═══ Back ═══ */}
                        <div
                           className="absolute inset-0 rounded-3xl p-7 flex flex-col justify-between bg-marble-100 border border-marble-200 shadow-xl transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                          style={{
                            backfaceVisibility: 'hidden',
                            transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(-180deg)',
                          }}
                        >
                          {/* Back header */}
                          <div>
                            <div className="flex items-center gap-3 mb-5">
                              <div className="h-9 w-9 rounded-xl bg-wine-800 flex items-center justify-center">
                                <span className="text-white font-bold text-xs font-en">{String(i + 1).padStart(2, '0')}</span>
                              </div>
                              <div className="h-[2px] flex-1 bg-gradient-to-l from-marble-200 to-transparent" />
                            </div>
                            <p className="text-ink text-[14px] leading-[1.9]">{item.a}</p>
                          </div>

                          {/* Back footer */}
                          <div className="flex items-center gap-2 text-ink-hint text-xs font-medium pt-4 border-t border-marble-200">
                            <span>{isAr ? 'اضغط للعودة' : 'Tap to flip back'}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15m0 0 6.75-6.75M3.75 12h15m0 0-6.75 6.75" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            )
          })()}

          {/* ═══ Bottom hint ═══ */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center text-sm text-ink-hint mt-10"
          >
            {isAr ? 'اضغط على أي بطاقة للإجابة' : 'Click any card to reveal the answer'}
          </motion.p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          SECTION 9 — DOWNLOAD APP (Clean CTA banner)
          ═══════════════════════════════════════════════════════ */}
      <section id="download" className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2.5rem] overflow-hidden bg-wine-900"
          >
            {/* Damascene medallion */}
            <svg className="absolute -end-16 -top-16 w-64 h-64 opacity-[0.04] pointer-events-none" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="100" cy="100" r="95" stroke="currentColor" strokeWidth="0.4" className="text-gold-400" />
              <circle cx="100" cy="100" r="75" stroke="currentColor" strokeWidth="0.4" className="text-gold-400" />
              <circle cx="100" cy="100" r="55" stroke="currentColor" strokeWidth="0.4" className="text-gold-400" />
              <circle cx="100" cy="100" r="35" stroke="currentColor" strokeWidth="0.4" className="text-gold-400" />
              <path d="M100 5 L100 195 M5 100 L195 100 M22 22 L178 178 M178 22 L22 178" stroke="currentColor" strokeWidth="0.25" className="text-gold-400" />
              {[0,30,60,90,120,150,180,210,240,270,300,330].map((deg) => (
                <circle key={deg} cx={100 + 88 * Math.cos(deg * Math.PI / 180)} cy={100 + 88 * Math.sin(deg * Math.PI / 180)} r="5" stroke="currentColor" strokeWidth="0.3" fill="none" className="text-gold-400" />
              ))}
              {[0,45,90,135,180,225,270,315].map((deg) => (
                <circle key={`inner-${deg}`} cx={100 + 65 * Math.cos(deg * Math.PI / 180)} cy={100 + 65 * Math.sin(deg * Math.PI / 180)} r="12" stroke="currentColor" strokeWidth="0.3" fill="none" className="text-gold-400" />
              ))}
            </svg>
            {/* Ambient glow */}
            <div className="absolute top-0 end-0 w-[400px] h-[400px] bg-gold-400/5 rounded-full blur-[100px] pointer-events-none" />
            {/* Content */}
            <div className="relative z-10 px-8 sm:px-12 md:px-16 py-14 md:py-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Left — Text */}
                <div>
                  <span className="inline-block px-3.5 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-gold-400 bg-gold-400/10 rounded-full border border-gold-400/20 mb-5">
                    {t('cta.label')}
                  </span>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-4">
                    {t('cta.title')}
                    <br />
                    <span className="text-gold-400">{t('cta.titleHighlight')}</span>
                  </h2>
                  <p className="text-white/45 text-base sm:text-lg leading-relaxed mb-8 max-w-lg">
                    {t('cta.subtitle')}
                  </p>

                  {/* Download buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="/download"
                      download="sakk-wallet.apk"
                      className="inline-flex items-center gap-3 px-8 py-4 bg-wine-800 text-white rounded-2xl hover:bg-wine-700 hover:shadow-lg transition-all font-bold group"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302L15.39 12l2.308-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302L5.864 2.658z"/>
                      </svg>
                      <div className="flex flex-col items-start">
                        <span className="text-[8px] text-white/50 uppercase tracking-wider">{isAr ? 'تحميل مباشر' : 'Direct download'}</span>
                        <span className="text-sm font-bold">{isAr ? 'تطبيق أندرويد (APK)' : 'Android app (APK)'}</span>
                      </div>
                    </a>
                    <div className="inline-flex items-center gap-3 px-8 py-4 bg-wine-800/30 text-white/40 rounded-2xl cursor-not-allowed border border-wine-800/40">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <div className="flex flex-col items-start">
                        <span className="text-[8px] uppercase tracking-wider">{isAr ? 'قريباً على' : 'Coming to'}</span>
                        <span className="text-sm font-bold">App Store</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right — Stats + social proof */}
                <div className="flex flex-col sm:flex-row lg:flex-col gap-6 lg:items-end lg:text-end">
                  {/* Stats row */}
                  <div className="flex gap-8 sm:gap-10">
                    {[
                      { value: '10K+', label: t('cta.socialProof.users') },
                      { value: '4.8', label: t('cta.socialProof.rating') },
                      { value: '$0', label: t('cta.free') },
                    ].map((s, i) => (
                      <div key={i} className="flex flex-col">
                        <span className="text-2xl sm:text-3xl font-extrabold text-white font-en">{s.value}</span>
                        <span className="text-[11px] text-white/35 mt-1">{s.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Stars */}
                  <div className="flex items-center gap-1.5 sm:justify-start lg:justify-end">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-gold-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="text-sm font-bold text-white/70 font-en ms-1">4.8</span>
                  </div>

                  {/* Avatars */}
                  <div className="flex items-center gap-3 sm:justify-start lg:justify-end">
                    <div className="flex -space-x-2">
                      {['bg-wine-600', 'bg-wine-500', 'bg-wine-700', 'bg-wine-400', 'bg-wine-600'].map((c, i) => (
                        <div key={i} className={`w-8 h-8 rounded-full ${c} border-2 border-wine-900 flex items-center justify-center`}>
                          <span className="text-[9px] text-white font-bold">{['أ', 'س', 'م', 'ل', 'ر'][i]}</span>
                        </div>
                      ))}
                    </div>
                    <span className="text-xs text-white/40">+{t('cta.socialProof.users')}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
